/**
 * AchatsDashboard — Tableau de bord pro des Achats
 *
 * Hiérarchie visuelle :
 *   • Ligne 1 (grand format) : Total du mois · À payer · À traiter · À réceptionner
 *   • Ligne 2 (format compact) : Moyenne/jour · Top catégorie · Top fournisseur · Catégories
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Wallet, ClipboardList, PackageCheck, AlertTriangle,
  TrendingUp, Award, Truck, Loader2, RefreshCw, Layers, X, Info,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");

const KPI_STYLES = {
  emerald: { border: "#10b981", accent: "rgba(16,185,129,0.14)", text: "text-emerald-300", iconBg: "bg-emerald-500/15" },
  amber:   { border: "#f59e0b", accent: "rgba(245,158,11,0.14)", text: "text-amber-300",   iconBg: "bg-amber-500/15" },
  rose:    { border: "#f43f5e", accent: "rgba(244,63,94,0.14)",  text: "text-rose-300",    iconBg: "bg-rose-500/15" },
  sky:     { border: "#0ea5e9", accent: "rgba(14,165,233,0.14)", text: "text-sky-300",     iconBg: "bg-sky-500/15" },
  violet:  { border: "#a78bfa", accent: "rgba(167,139,250,0.14)",text: "text-violet-300",  iconBg: "bg-violet-500/15" },
  slate:   { border: "#64748b", accent: "rgba(100,116,139,0.14)",text: "text-slate-300",   iconBg: "bg-slate-500/15" },
};

/** Grande carte KPI — pour les indicateurs prioritaires (responsive) */
function KpiCardLarge({ color = "slate", icon: Icon, label, value, hint, testid, onClick }) {
  const s = KPI_STYLES[color] || KPI_STYLES.slate;
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl p-3 sm:p-4 border transition-transform hover:-translate-y-0.5 min-w-0 w-full text-left ${onClick ? "cursor-pointer hover:shadow-xl" : ""}`}
      style={{
        background: `linear-gradient(135deg, ${s.accent}, rgba(15,23,42,0.85))`,
        borderColor: "rgba(71,85,105,0.5)",
        borderLeft: `4px solid ${s.border}`,
      }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] sm:text-[10.5px] font-extrabold uppercase tracking-[0.13em] sm:tracking-[0.15em] text-slate-400 leading-tight">
            {label}
          </p>
          <p className={`font-black font-mono mt-1.5 sm:mt-2 truncate text-[18px] sm:text-[26px] lg:text-[28px] leading-[1.05] tracking-[-0.02em] ${s.text}`}>
            {value}
          </p>
          {hint && (
            <p className="text-[10.5px] sm:text-[11.5px] text-slate-400 mt-1 sm:mt-1.5 truncate font-medium">
              {hint}{onClick && <span className="ml-1 opacity-60">· cliquer →</span>}
            </p>
          )}
        </div>
        <div className={`shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl ${s.iconBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${s.text}`} strokeWidth={2.2} />
        </div>
      </div>
    </Wrapper>
  );
}

/** Tuile compacte — pour les indicateurs secondaires (2ᵉ ligne) */
function KpiTileCompact({ color = "slate", icon: Icon, label, value, hint, testid }) {
  const s = KPI_STYLES[color] || KPI_STYLES.slate;
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/40 border border-slate-700/50 transition-colors hover:bg-slate-800/60"
      style={{ borderLeft: `3px solid ${s.border}` }}
      data-testid={testid}
    >
      <div className={`shrink-0 w-9 h-9 rounded-lg ${s.iconBg} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${s.text}`} strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 leading-tight">{label}</p>
        <p className={`font-black text-[15px] leading-tight font-mono mt-0.5 truncate ${s.text}`}>{value}</p>
        {hint && <p className="text-[10.5px] text-slate-500 mt-0.5 truncate">{hint}</p>}
      </div>
    </div>
  );
}

export default function AchatsDashboard({ dateFilter: extDateFilter = null, onDateFilterChange = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // 🆕 Modale détail KPI
  const [detailKpi, setDetailKpi] = useState(null); // "total_month" | "to_process" | "to_pay" | "to_receive"
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 🆕 Sélection de date : mode "month" (par défaut) | "range" (personnalisé)
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [dateMode, setDateMode] = useState(extDateFilter?.mode || "month");
  const [monthValue, setMonthValue] = useState(extDateFilter?.month || defaultMonth);
  const [startDate, setStartDate] = useState(extDateFilter?.start_date || "");
  const [endDate, setEndDate] = useState(extDateFilter?.end_date || "");

  // Params dérivés pour les appels API — mémorisés pour utilisation dans useEffect
  const dashboardParams = React.useMemo(() => {
    if (dateMode === "range" && startDate && endDate) {
      return { start_date: startDate, end_date: endDate };
    }
    // Mode mois
    const [y, m] = (monthValue || "").split("-");
    return { year: parseInt(y, 10), month: parseInt(m, 10) };
  }, [dateMode, monthValue, startDate, endDate]);

  const buildParams = () => dashboardParams;

  // 🆕 Propagation vers le parent (list sync)
  useEffect(() => {
    if (onDateFilterChange) {
      onDateFilterChange({
        mode: dateMode,
        month: monthValue,
        start_date: startDate,
        end_date: endDate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateMode, monthValue, startDate, endDate]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/expenses/dashboard`, { params: dashboardParams });
      setData(r.data);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [dashboardParams]);

  const openDetail = async (kpi) => {
    setDetailKpi(kpi);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/expenses/dashboard/detail`, { params: { kpi, ...dashboardParams } });
      setDetailData(r.data);
    } catch (e) { /* silent */ }
    finally { setDetailLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh 1min
    return () => clearInterval(t);
  }, [load]);

  if (!data) {
    return (
      <div className="p-4 rounded-xl bg-slate-900/40 border border-dashed border-slate-700 text-slate-500 text-center text-[13px]">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement du tableau de bord…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="achats-dashboard">
      <div className="flex items-center justify-between px-1 gap-2 flex-wrap">
        <p className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-purple-300 flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
          Tableau de bord — {data.period}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="achats-date-picker">
          {/* Toggle mode : Mois / Personnalisé */}
          <div className="inline-flex rounded-md overflow-hidden border border-slate-700 h-8">
            <button
              type="button"
              onClick={() => setDateMode("month")}
              className={`px-2.5 text-[10.5px] font-bold uppercase tracking-wider transition-colors ${
                dateMode === "month" ? "bg-purple-500/25 text-purple-100" : "bg-slate-800/60 text-slate-400 hover:text-purple-200"
              }`}
              data-testid="achats-date-mode-month"
            >
              Mois
            </button>
            <button
              type="button"
              onClick={() => {
                setDateMode("range");
                // 🆕 Auto-remplissage si dates vides — 1er et dernier jour du mois courant
                if (!startDate || !endDate) {
                  const [y, m] = (monthValue || "").split("-").map(Number);
                  if (y && m) {
                    const firstDay = `${monthValue}-01`;
                    const lastDay = new Date(y, m, 0).getDate();
                    setStartDate(firstDay);
                    setEndDate(`${monthValue}-${String(lastDay).padStart(2, "0")}`);
                  }
                }
              }}
              className={`px-2.5 text-[10.5px] font-bold uppercase tracking-wider border-l border-slate-700 transition-colors ${
                dateMode === "range" ? "bg-purple-500/25 text-purple-100" : "bg-slate-800/60 text-slate-400 hover:text-purple-200"
              }`}
              data-testid="achats-date-mode-range"
            >
              Personnalisé
            </button>
          </div>

          {/* Sélecteur adapté au mode */}
          {dateMode === "month" ? (
            <input
              type="month"
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              className="h-8 bg-slate-800 border border-slate-700 rounded-md px-2 text-white text-[12px] focus:border-purple-500/60 focus:outline-none"
              data-testid="achats-date-month-input"
            />
          ) : (
            <div className="inline-flex items-center gap-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8 bg-slate-800 border border-slate-700 rounded-md px-2 text-white text-[12px] focus:border-purple-500/60 focus:outline-none"
                data-testid="achats-date-start"
                title="Date de début"
              />
              <span className="text-slate-500 text-[11px]">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8 bg-slate-800 border border-slate-700 rounded-md px-2 text-white text-[12px] focus:border-purple-500/60 focus:outline-none"
                data-testid="achats-date-end"
                title="Date de fin"
              />
            </div>
          )}

          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-800/60 border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700/70 transition-colors"
            title="Actualiser"
            data-testid="achats-dashboard-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={2.4} />}
          </button>
        </div>
      </div>

      {/* LIGNE 1 — KPI prioritaires (grand format) — CLIQUABLES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCardLarge
          color="emerald"
          icon={Wallet}
          label="Total du mois"
          value={`${fmt(data.total_month)} F`}
          hint={`${data.count_month} achat${data.count_month > 1 ? 's' : ''}`}
          testid="kpi-total-month"
          onClick={() => openDetail("total_month")}
        />
        <KpiCardLarge
          color="amber"
          icon={ClipboardList}
          label="À traiter"
          value={`${fmt(data.to_process.total)} F`}
          hint={`${data.to_process.count} demande${data.to_process.count > 1 ? 's' : ''} en attente`}
          testid="kpi-to-process"
          onClick={() => openDetail("to_process")}
        />
        <KpiCardLarge
          color="rose"
          icon={AlertTriangle}
          label="À payer"
          value={`${fmt(data.to_pay.total)} F`}
          hint={`${data.to_pay.count} facture${data.to_pay.count > 1 ? 's' : ''}`}
          testid="kpi-to-pay"
          onClick={() => openDetail("to_pay")}
        />
        <KpiCardLarge
          color="sky"
          icon={PackageCheck}
          label="À réceptionner"
          value={`${fmt(data.to_receive.total)} F`}
          hint={`${data.to_receive.count} attendu${data.to_receive.count > 1 ? 's' : ''}`}
          testid="kpi-to-receive"
          onClick={() => openDetail("to_receive")}
        />
      </div>

      {/* LIGNE 2 — KPI secondaires (compact) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <KpiTileCompact
          color="violet"
          icon={TrendingUp}
          label="Moyenne / jour"
          value={`${fmt(data.avg_daily)} F`}
          hint="Depuis le début du mois"
        />
        <KpiTileCompact
          color="emerald"
          icon={Award}
          label="Top catégorie"
          value={data.top_category.name}
          hint={`${fmt(data.top_category.total)} F`}
        />
        <KpiTileCompact
          color="sky"
          icon={Truck}
          label="Top fournisseur"
          value={(data.top_supplier.name || "—").slice(0, 22)}
          hint={`${fmt(data.top_supplier.total)} F`}
        />
        {data.categories_breakdown && data.categories_breakdown.length > 0 ? (
          <KpiTileCompact
            color="slate"
            icon={Layers}
            label="Catégories"
            value={data.categories_breakdown.length}
            hint={data.categories_breakdown.slice(0, 3).map(c => c.category).join(" · ")}
          />
        ) : (
          <KpiTileCompact color="slate" icon={Layers} label="Catégories" value="—" hint="Aucun achat classé" />
        )}
      </div>

      {/* 🆕 Modale de détail KPI (clic sur une bulle) */}
      <Dialog open={!!detailKpi} onOpenChange={(v) => { if (!v) { setDetailKpi(null); setDetailData(null); } }}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 56rem)" }}
          data-testid="achats-kpi-detail"
        >
          <DialogHeader>
            <DialogTitle className="text-purple-300 flex items-center gap-2 text-lg">
              <Info className="w-5 h-5" />
              {detailData?.title || "Détail"}
              {detailData && (
                <span className="ml-auto rounded-full px-3 py-1 border bg-purple-500/20 border-purple-500/40 text-purple-100 text-[13px] font-black tabular-nums">
                  {fmt(detailData.total)} F · {detailData.count}
                </span>
              )}
            </DialogTitle>
            {detailData?.hint && (
              <p className="text-slate-400 text-[11.5px] pt-1 leading-snug">{detailData.hint}</p>
            )}
          </DialogHeader>

          <div className="mt-3">
            {detailLoading && (
              <p className="text-slate-500 text-[12px] text-center py-6">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement du détail…
              </p>
            )}
            {detailData && detailData.items?.length === 0 && (
              <p className="text-slate-500 text-[12px] italic text-center py-6">Aucun document dans cette catégorie.</p>
            )}
            {detailData && detailData.items?.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-slate-700">
                <table className="w-full text-[12px]" data-testid="achats-kpi-detail-table">
                  <thead className="bg-slate-800/70 text-slate-400 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="text-left px-2 py-2">Date</th>
                      <th className="text-left px-2 py-2">Nº</th>
                      <th className="text-left px-2 py-2">Fournisseur</th>
                      <th className="text-left px-2 py-2">Catégorie</th>
                      <th className="text-left px-2 py-2">Type</th>
                      <th className="text-left px-2 py-2">Statut</th>
                      <th className="text-right px-2 py-2">Montant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {detailData.items.map((it) => (
                      <tr key={it.id} className="hover:bg-slate-800/40 transition-colors">
                        <td className="px-2 py-1.5 text-slate-400 tabular-nums whitespace-nowrap">{(it.created_at || "").slice(0, 10)}</td>
                        <td className="px-2 py-1.5 text-slate-300 whitespace-nowrap font-mono">{it.invoice_number || "—"}</td>
                        <td className="px-2 py-1.5 text-white font-semibold">{it.supplier || "—"}</td>
                        <td className="px-2 py-1.5 text-slate-400 capitalize">{it.category || "—"}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            it.expense_type === "paiement" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                            : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                          }`}>
                            {it.expense_type === "paiement" ? "Paiement" : "Achat"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="text-slate-300 text-[10.5px] uppercase">{it.status}</span>
                          {it.is_paid && <span className="ml-1 px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[9.5px]">payé</span>}
                          {it.to_stock && !it.stock_received_at && <span className="ml-1 px-1 py-0.5 rounded bg-sky-500/20 text-sky-300 text-[9.5px]">à recevoir</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right text-white font-black tabular-nums">{fmt(it.amount)} F</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-800/50">
                    <tr>
                      <td colSpan={6} className="px-2 py-2 text-right text-slate-400 font-bold uppercase text-[10.5px] tracking-wider">Total</td>
                      <td className="px-2 py-2 text-right text-purple-200 font-black text-[13px] tabular-nums">{fmt(detailData.total)} F</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              variant="outline"
              onClick={() => { setDetailKpi(null); setDetailData(null); }}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 h-9"
              data-testid="achats-kpi-detail-close"
            >
              <X className="w-4 h-4 mr-1.5" /> Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
